import { chain, chunk } from 'lodash';
import DEFAULT_AVATAR from '../../../assets/img/avatar.png';
import { getProfileImage } from '../../../utils';
import moment from 'moment';
import { DATE_TIME_FORMATS } from '../../../constants';

export const CURRENT_VIEW = {
    CHAT_VIEW: 'CHAT_VIEW',
    MESSAGE_VIEW: 'MESSAGE_VIEW',
    NEW_CHAT_VIEW: 'NEW_CHAT_VIEW',
};

export const MESSAGE_ADD_TYPE = {
    APPEND: 'append',
    PREPEND: 'prepend',
};

export async function runInBatches(tasks, batchSize = 20) {
    const results = [];

    const batches = chunk(tasks, batchSize);

    for (const batch of batches) {
        const res = await Promise.all(batch.map((fn) => fn()));
        results.push(...res);
    }

    return results;
}

/**
 * Fetches presigned avatar URLs for users and replaces s3_key with the actual URL
 * @param {Array} users - Array of user objects containing s3_key for avatar
 * @returns {Promise<Array>} - Updated users with avatar URLs
 */
export async function enrichUsersWithAvatarUrls({ data }) {
    // Filter users who have an avatar s3_key
    const users = [...data];

    const usersWithAvatars = users?.filter(
        (user) => !!user?.avatar || typeof user.avatar === 'string',
    );
    const usersWithoutAvatars = users
        ?.filter((user) => !user?.avatar || typeof user.avatar !== 'string')
        .map((user) => ({ ...user, avatar: DEFAULT_AVATAR }));

    // Create batch tasks for fetching avatars
    const avatarFetchTasks = usersWithAvatars.map(
        (user) => () =>
            getProfileImage({ profileImg: user.avatar })
                .then((url) => ({ userId: user.id, url }))
                .catch(() => ({ userId: user.id, url: DEFAULT_AVATAR })), // Fallback if fetch fails
    );

    // Process in batches to avoid rate limiting
    const avatarResults = await runInBatches(avatarFetchTasks);

    // Create a mapping of user IDs to avatar URLs
    const avatarMap = chain(avatarResults).keyBy('userId').mapValues('url').value();
    const usersWithUrls = usersWithAvatars.map((user) => ({
        ...user,
        avatar: avatarMap[user.id] || DEFAULT_AVATAR, // Use fetched URL or default
    }));

    // Return new array with updated avatar URLs
    return [...usersWithUrls, ...usersWithoutAvatars];
}

export const USER_MESSAGES = [
    {
        position: 'left',
        type: 'text',
        text: 'Hello! How can I help you?',
        date: new Date(),
    },
    {
        position: 'right',
        type: 'text',
        text: 'Hello! How can I help you?',
        date: new Date(),
    },
];

export const DEFAULT_AVATAR_URL = DEFAULT_AVATAR;

export const formattedMessageDate = (date) => {
    const msgDate = moment(date);
    const now = moment();
    let dateString = '';

    if (msgDate.isSame(now, 'day')) {
        // Today
        dateString = `Today, ${msgDate.format(DATE_TIME_FORMATS.TIME_12HRS)}`;
    } else if (msgDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
        // Yesterday
        dateString = `Yesterday, ${msgDate.format(DATE_TIME_FORMATS.TIME_12HRS)}`;
    } else if (msgDate.isSame(now, 'year')) {
        dateString = msgDate.format(`MM/DD, ${DATE_TIME_FORMATS.TIME_12HRS}`);
    } else {
        dateString = msgDate.format(DATE_TIME_FORMATS.DATE_TIME_12HRS);
    }
    
    return dateString;
};
